import { Link } from 'react-router';
import { Bouton } from '../shared/ui/Bouton';
import { Photo, Video } from '../shared/ui/Media';
import { Paragraphe, Section } from '../shared/ui/Section';
import { Titre } from '../shared/ui/Titre';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **PAGE 4 DES HUIT — LE DÉTAIL DU PRODUIT, ET C'EST CELLE QUI PORTE LES MÉDIAS.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * F-69 dans sa table : « page du **détail du produit** — porte les deux vidéos et les photos
 * générées, **aucune capture réelle** ». Le brief la nomme S-17, avec la même mention.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **« AUCUNE CAPTURE D'ÉCRAN RÉELLE » EST LE FAIT LE PLUS LOURD DE `CDC-6`, ET C'EST
 * SUR CETTE PAGE QU'IL SE JOUE.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * `CDC-6` en tête de document : « **le fait le plus lourd de ce document, et il ferme un
 * risque par construction plutôt que par une règle** : **aucune capture d'écran réelle du
 * produit ne paraît sur cette surface**, donc aucune écriture manuscrite, aucun prénom, aucun
 * titre de cours, aucun contenu de tableau, aucune donnée de mineur. Le produit conserve
 * l'écriture manuscrite de personnes dont il ne sait pas qui est mineure ; **aucune de ces
 * traces n'atteint la surface publique, et ce n'est pas parce qu'on les filtre, c'est parce
 * qu'on n'en met aucune.** »
 *
 * **Cette page est donc la plus exposée du site au sens du contenu**, et pas au sens
 * technique : c'est celle où quelqu'un, un jour, voudra « montrer le vrai produit ». Le motif
 * est écrit ici pour que ce jour-là il se lise avant de coller un fichier dans
 * `site/public/media/`.
 *
 * **Et les photos ne représentent personne** : F-74 — « les photos sont générées **hors du
 * produit, une fois, à la fabrication du contenu**, et livrées comme des fichiers avec le
 * code. Rien du produit n'appelle un modèle de langage ou d'image, à l'exécution ni ailleurs.
 * Conséquence qui va dans le bon sens : **ces images ne représentent aucune personne
 * réelle** ». Voir `shared/ui/Media.tsx` : rien de ce dépôt ne peut tenir cette règle, elle
 * tient parce que les fichiers sont produits par une personne qui décide.
 */
export function PageProduit() {
  return (
    <>
      <Section largeur="etroite">
        <Titre niveau={1}>Le produit, en détail</Titre>
        <Paragraphe>
          Swype fait quatre choses : un tableau qu’on écrit à la main à
          plusieurs, une visio d’appoint, la mesure du temps de cours, et un
          endroit où retrouver ce qui a été fait. Voici comment.
        </Paragraphe>
      </Section>

      {/* **La seconde des deux vidéos**, F-74. Servie depuis le domaine du produit, jamais
          embarquée d'un hébergeur : « un lecteur tiers enverrait l'adresse réseau de chaque
          visiteur à ce tiers, sur une surface qui vient de refuser toute mesure
          d'audience » (F-73). */}
      <Section largeur="large">
        <Video
          fichier="produit.mp4"
          description="Le tableau pendant un cours : deux traits qui apparaissent en même temps, la visio dans un coin."
        />
      </Section>

      <Section titre="Le tableau">
        <Paragraphe>
          Une page blanche, un stylet, cinq encres et un surligneur. Le tuteur et
          l’élève écrivent en même temps et voient le trait de l’autre
          apparaître. Un cours peut compter plusieurs tableaux, et on circule de
          l’un à l’autre.
        </Paragraphe>
        <Photo
          fichier="tableau.webp"
          description="Une page de tableau couverte d’une démonstration écrite au stylet, en deux couleurs."
        />
      </Section>

      <Section titre="La visio, quand elle sert">
        <Paragraphe>
          La voix et l’image s’allument depuis le tableau et s’éteignent de la
          même façon. Elles ne prennent jamais la place du tracé : c’est lui
          qu’on regarde pendant un cours.
        </Paragraphe>
      </Section>

      <Section titre="Le temps de cours">
        <Paragraphe>
          Chaque entrée et chaque sortie d’un participant sont relevées, et la
          durée en découle. Personne ne saisit ses heures, personne ne les
          conteste : elles sont mesurées.
        </Paragraphe>
      </Section>

      <Section titre="Après le cours">
        <Paragraphe>
          Le tableau reste consultable pendant une durée réglée par la
          structure. Les documents échangés vivent dans un espace de fichiers, et
          les conversations restent accessibles entre deux cours.
        </Paragraphe>
        <Photo
          fichier="espace.webp"
          description="La liste des cours passés d’un élève, avec la durée de chacun."
        />
      </Section>

      <Section largeur="etroite" titre="Sur quel appareil">
        <Paragraphe>
          Le tableau demande un écran d’au moins 1024 points de large, en
          paysage, et un stylet ou un doigt. Ordinateur et tablette, donc. Ce
          site, lui, se lit partout.
        </Paragraphe>
      </Section>

      <Section largeur="etroite">
        <Link to="/contact">
          <Bouton>Nous écrire</Bouton>
        </Link>
      </Section>
    </>
  );
}
